import React, { useState, useMemo } from 'react';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Calendar } from './calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { CalendarIcon, ChevronDown } from 'lucide-react';
import { format, startOfDay, endOfDay, startOfWeek, startOfMonth, endOfMonth, subDays, subMonths } from 'date-fns';

// Date preset options
const DATE_PRESETS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'custom', label: 'Custom Range' }
];

// Calculate date range based on preset
const getDateRange = (preset) => {
  const now = new Date();
  const today = startOfDay(now);
  
  switch (preset) {
    case 'all':
      return { startDate: null, endDate: null };
    case 'today':
      return { startDate: today, endDate: endOfDay(now) };
    case 'yesterday':
      const yesterday = subDays(today, 1);
      return { startDate: yesterday, endDate: endOfDay(yesterday) };
    case 'this_week':
      return { startDate: startOfWeek(now, { weekStartsOn: 1 }), endDate: endOfDay(now) };
    case 'this_month':
      return { startDate: startOfMonth(now), endDate: endOfDay(now) };
    case 'last_month':
      const lastMonth = subMonths(now, 1);
      return { startDate: startOfMonth(lastMonth), endDate: endOfMonth(lastMonth) };
    default:
      return { startDate: null, endDate: null };
  }
};

export function DateRangeFilter({ 
  onDateChange,
  defaultPreset = 'all',
  className = '',
  compact = false 
}) {
  const [selectedPreset, setSelectedPreset] = useState(defaultPreset);
  const [customRange, setCustomRange] = useState({ from: undefined, to: undefined });
  const [isCustomOpen, setIsCustomOpen] = useState(false);

  // Handle preset change
  const handlePresetChange = (preset) => {
    setSelectedPreset(preset);
    
    if (preset === 'custom') {
      setIsCustomOpen(true);
      return;
    }
    
    const { startDate, endDate } = getDateRange(preset);
    const toLocal = (d) => {
      if (!d) return null;
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };
    onDateChange?.({
      startDate: toLocal(startDate),
      endDate: toLocal(endDate),
      preset
    });
  };

  // Handle custom range selection
  const handleCustomRangeSelect = (range) => {
    setCustomRange(range);
    
    if (range?.from && range?.to) {
      const toLocal = (d) => {
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      };
      onDateChange?.({
        startDate: toLocal(startOfDay(range.from)),
        endDate: toLocal(endOfDay(range.to)),
        preset: 'custom'
      });
    }
  };

  // Get display label
  const getDisplayLabel = useMemo(() => {
    if (selectedPreset === 'custom' && customRange.from && customRange.to) {
      return `${format(customRange.from, 'MMM d')} - ${format(customRange.to, 'MMM d, yyyy')}`;
    }
    return DATE_PRESETS.find(p => p.value === selectedPreset)?.label || 'All Time';
  }, [selectedPreset, customRange]);

  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Popover>
          <PopoverTrigger asChild>
            <Button 
              variant="outline" 
              size="sm"
              className="h-9 px-3 text-sm font-normal justify-between min-w-[160px]"
              data-testid="date-filter-trigger"
            >
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-gray-500" />
                <span className="text-gray-700">{getDisplayLabel}</span>
              </div>
              <ChevronDown className="h-4 w-4 text-gray-500" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="flex">
              {/* Presets List */}
              <div className="w-40 border-r">
                {DATE_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => handlePresetChange(preset.value)}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 transition-colors ${
                      selectedPreset === preset.value 
                        ? 'bg-blue-50 text-blue-700 font-medium' 
                        : 'text-gray-700'
                    }`}
                    data-testid={`date-preset-${preset.value}`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              
              {/* Custom Calendar */}
              {selectedPreset === 'custom' && (
                <div className="p-2">
                  <Calendar
                    mode="range"
                    selected={customRange}
                    onSelect={handleCustomRangeSelect}
                    numberOfMonths={2}
                    disabled={{ after: new Date() }}
                    className="rounded-md"
                  />
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  // Full mode with separate dropdown and custom picker
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Select value={selectedPreset} onValueChange={handlePresetChange}>
        <SelectTrigger 
          className="w-[180px] h-9"
          data-testid="date-filter-select"
        >
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-gray-500" />
            <SelectValue placeholder="Select date range" />
          </div>
        </SelectTrigger>
        <SelectContent>
          {DATE_PRESETS.map((preset) => (
            <SelectItem 
              key={preset.value} 
              value={preset.value}
              data-testid={`date-option-${preset.value}`}
            >
              {preset.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Custom Date Picker Popover */}
      {selectedPreset === 'custom' && (
        <Popover open={isCustomOpen} onOpenChange={setIsCustomOpen}>
          <PopoverTrigger asChild>
            <Button 
              variant="outline" 
              size="sm"
              className="h-9"
              data-testid="custom-date-picker-trigger"
            >
              {customRange.from && customRange.to 
                ? `${format(customRange.from, 'MMM d')} - ${format(customRange.to, 'MMM d, yyyy')}`
                : 'Pick dates'
              }
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={customRange}
              onSelect={handleCustomRangeSelect}
              numberOfMonths={2}
              disabled={{ after: new Date() }}
              className="rounded-md"
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

export { DATE_PRESETS, getDateRange };
